import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Refresh as RefreshIcon } from '@material-ui/icons';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';

import BankIDLogo from '../assets/BankID_MasterBrand/BankID_logo.svg';
import BankIDLogoWhite from '../assets/BankID_MasterBrand/BankID_logo_white.svg';
import GlimraStore from '../store/GlimraStore';
import { BankIDLegalText, BankIDSession, BankIDStatus } from '../types/bankID';

import RegistrationCard from './helpers/RegistrationCard';
import SpacerVertical from './helpers/SpacerVertical';

const POLL_INTERVAL = 1000;
const QR_REFRESH_INTERVAL = 1000;

interface BankIDSignProps {
  legal_text_key: string;
  onSuccess?: (request: BankIDSession) => void;
  onReset?: () => void;
}

export function BankID({ legal_text_key, onSuccess, onReset }: BankIDSignProps) {
  const { t } = useTranslation();
  const [bankIDSession, setBankIDSession] = useState<BankIDSession>();
  const [bankIDStatus, setBankIDStatus] = useState<BankIDStatus>();
  const [legalText, setLegalText] = useState<BankIDLegalText>();
  const [polling, setPolling] = useState<NodeJS.Timeout>();
  const [error, setError] = useState<string>();
  const [startTime, setStartTime] = useState(0);
  const [showQRCode, setShowQRCode] = useState(false);
  const [legalTextModalOpen, setLegalTextModalOpen] = useState(false);

  const fetchLegalText = useCallback(() => {
    (async () => {
      const response = await GlimraStore.getBankIDLegalText(legal_text_key);
      setLegalText(response.data);
    })();
  }, [legal_text_key]);

  useEffect(() => {
    fetchLegalText();
  }, [fetchLegalText, legal_text_key]);

  const createOrRetrySession = useCallback(() => {
    (async (): Promise<void> => {
      try {
        if (!legalText) {
          fetchLegalText();
          setError(t('bankID.createRequestError'));
          return;
        }
        const response = !bankIDSession
          ? await GlimraStore.createBankIDSign(legalText)
          : await GlimraStore.retryBankIDRequest(bankIDSession.uuid);
        setBankIDSession(response.data);
        setStartTime((v) => (v === 0 ? Date.now() : v));
      } catch (e) {
        setError(t('bankID.createRequestError'));
      }
    })();
  }, [bankIDSession, fetchLegalText, legalText, t]);

  const fetchStatus = useCallback(() => {
    (async (): Promise<void> => {
      if (showQRCode) return;
      if (bankIDSession) {
        const response = await GlimraStore.getBankIDStatus(bankIDSession?.uuid);
        setBankIDStatus(response.data.status);
      }
    })();
  }, [bankIDSession, showQRCode]);

  const startPolling = useCallback(() => {
    fetchStatus();
    setPolling(setInterval(fetchStatus, POLL_INTERVAL));
  }, [fetchStatus]);

  const resetState = useCallback(async (): Promise<void> => {
    setBankIDStatus(undefined);
    setError(undefined);
    clearInterval(polling);
    setPolling(undefined);
    setStartTime(0);
  }, [polling]);

  const handleRetry = useCallback(async (): Promise<void> => {
    await resetState();
    createOrRetrySession();
  }, [createOrRetrySession, resetState]);

  const resetStartTime = () => {
    setStartTime(Date.now());
  };

  const handleToggleQRCode = useCallback(
    (show: boolean) => {
      if (show) {
        if (!bankIDSession) createOrRetrySession();
        if (polling) clearInterval(polling);
        setShowQRCode(show);
      } else {
        resetState();
        resetStartTime();
        setShowQRCode(show);
      }
    },
    [bankIDSession, createOrRetrySession, polling, resetState]
  );

  const handleSignPress = (): void => {
    if (!bankIDSession) createOrRetrySession();
    handleToggleQRCode(false);
    const url = `https://app.bankid.com/?autostarttoken=${bankIDSession?.auto_start_token}&redirect=null`;
    window.open(url, '_blank');
    startPolling();
  };

  useEffect(() => {
    if (bankIDStatus === BankIDStatus.COMPLETE && bankIDSession) {
      resetState();
      handleToggleQRCode(false);
      if (onSuccess) onSuccess(bankIDSession);
    }
  }, [bankIDSession, bankIDStatus, resetState, handleToggleQRCode, onSuccess, polling]);

  useEffect(() => {
    if (error && showQRCode) {
      resetStartTime();
      setShowQRCode(false);
    }
  }, [error, showQRCode]);

  return (
    <>
      <Grid item xs={12} md={8}>
        <RegistrationCard container spacing={2} style={{ placeContent: 'flex-start' }}>
          <Grid item xs={12}>
            <Grid container direction="row" justify="space-between">
              <Grid item>
                <Typography variant="h6">{t('bankID.card3')}</Typography>
                <Typography variant="h5">{t('bankID.card3Title')}</Typography>
                <SpacerVertical size="small" />
              </Grid>
              <Grid item>
                <IconButton onClick={onReset}>
                  <RotateLeftIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ flexGrow: 1 }}>
            <Grid container direction="column" spacing={2} style={{ gap: 8 }}>
              <QRComponent
                show={showQRCode}
                requestUUID={bankIDSession?.uuid}
                retry={handleRetry}
                startTime={startTime}
                resetStartTime={resetStartTime}
                setError={setError}
                setBankIDStatus={setBankIDStatus}
              />

              {!error && (
                <>
                  <Typography
                    onClick={() => setLegalTextModalOpen(true)}
                    align={'center'}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    {t('bankID.agreementToSign')}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleToggleQRCode(!showQRCode)}
                    fullWidth
                    disableElevation
                  >
                    <Box display="inline-flex">
                      <img src={BankIDLogoWhite} alt={'BankID Logo'} width={24} height={24} />
                      <Box my="auto">{showQRCode ? t('bankID.hideQR') : t('bankID.showQR')}</Box>
                    </Box>
                  </Button>
                  <Box display="flex" width={'100%'} alignItems={'center'}>
                    <Divider style={{ flexGrow: 1 }} />
                    <Typography color={'textSecondary'} style={{ paddingLeft: 8, paddingRight: 8 }}>
                      {t('bankID.or')}
                    </Typography>
                    <Divider style={{ flexGrow: 1 }} />
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSignPress}
                    disabled={showQRCode}
                    fullWidth
                  >
                    {polling === undefined ? (
                      t('bankID.openBankIDOnThisDevice')
                    ) : (
                      <CircularProgress color="secondary" size={24} />
                    )}
                  </Button>
                </>
              )}
              {!!error && (
                <>
                  <Typography align="center" color="error">
                    {error}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      handleRetry();
                      resetStartTime();
                    }}
                    fullWidth
                  >
                    {t('bankID.retry')}
                  </Button>
                </>
              )}
            </Grid>
          </Grid>
        </RegistrationCard>
      </Grid>
      <LegalTextModal
        legalText={legalText?.text ?? ''}
        open={legalTextModalOpen}
        setOpen={setLegalTextModalOpen}
      />
    </>
  );
}

const qrStyles = makeStyles(() => ({
  qrButton: {
    opacity: (props: { manualRetryRequired: boolean }) => (props.manualRetryRequired ? 0.25 : 1),
    padding: 0,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  },
  qrPlaceholder: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid black',
  },
  retryIcon: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    background: 'transparent',
    fontSize: '9rem',
  },
  fullscreenOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(255, 255, 255, 1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20,
  },
}));

interface QRComponentProps {
  show: boolean;
  requestUUID: string | undefined;
  retry: () => Promise<void>;
  startTime: number;
  resetStartTime: () => void;
  setError: (message: string) => void;
  setBankIDStatus: (bankIDStatus: BankIDStatus | undefined) => void;
}

export function QRComponent({
  show,
  requestUUID,
  retry,
  startTime,
  resetStartTime,
  setError,
  setBankIDStatus,
}: QRComponentProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<string>();
  const [manualRetryRequired, setManualRetryRequired] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [screenReaderCountdownAnnouncement, setScreenReaderCountdownAnnouncement] =
    useState<string>('');
  const [fullscreen, setFullscreen] = useState(false);
  const [scanError, setScanError] = useState('');
  const mainMessageRef = useRef<HTMLDivElement>(null);
  const qrButtonRef = useRef<HTMLButtonElement>(null);
  const timeLeft = Math.floor(300 - (Date.now() - startTime) / 1000);
  const allowExtend = timeLeft < 30 && !manualRetryRequired && !timedOut;

  const qrClasses = qrStyles({ manualRetryRequired });

  const handleRetry = useCallback(() => {
    (async (): Promise<void> => {
      setScanError('');
      setManualRetryRequired(false);
      await retry();
      setTimedOut(false);
    })();
  }, [retry]);

  const getData = useCallback(() => {
    (async () => {
      if (!requestUUID) return;

      const elapsed = (Date.now() - startTime) / 1000;
      if (elapsed > 300) {
        setTimedOut(true);
        setManualRetryRequired(true);
        setScanError(t('bankID.sessionExpiredError'));
        return;
      }

      try {
        const response = await GlimraStore.getBankIDQRData(requestUUID);
        setBankIDStatus(response.data.status);
        setData(response.data.qr_data);
      } catch {
        setError(t('bankID.unknownFetchQRError'));
      }
    })();
  }, [requestUUID, startTime, setBankIDStatus, setError, t]);

  useEffect(() => {
    if (!requestUUID || timedOut || !show) return;
    const interval = setInterval(getData, QR_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [getData, requestUUID, show, timedOut]);

  useEffect(() => {
    // Handle spacebar to extend time
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        if (allowExtend) {
          e.preventDefault();
          resetStartTime();
        }
        if (manualRetryRequired) {
          e.preventDefault();
          handleRetry();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [allowExtend, handleRetry, manualRetryRequired, resetStartTime]);

  useEffect(() => {
    // Announce at key intervals
    const seconds = timeLeft % 60;
    const minutes = Math.floor(timeLeft / 60);

    if ([5, 4, 3, 2, 1].includes(minutes) && seconds === 0) {
      setScreenReaderCountdownAnnouncement(`${minutes} ${t('bankID.minutesRemaining')}`);
    }

    if (minutes === 0 && [50, 40, 30, 20, 10].includes(seconds)) {
      setScreenReaderCountdownAnnouncement(`${seconds}  ${t('bankID.secondsRemaining')}`);
    }
  }, [t, timeLeft]);

  return (
    <>
      {show && (
        <>
          <QRButton
            size={256}
            qrButtonRef={qrButtonRef}
            manualRetryRequired={manualRetryRequired}
            data={data}
            handleRetry={handleRetry}
            timeLeft={timeLeft}
            allowExtend={allowExtend}
            resetStartTime={resetStartTime}
            onClick={() => setFullscreen(true)}
          />
          <Box
            aria-live="polite"
            className="sr-only"
            component={() => <div ref={mainMessageRef} />}
          >
            {screenReaderCountdownAnnouncement}
          </Box>
          <Typography color="error">{scanError}</Typography>
        </>
      )}
      {fullscreen && (
        <Box onClick={() => setFullscreen(false)} className={qrClasses.fullscreenOverlay}>
          <QRButton
            size={320}
            qrButtonRef={qrButtonRef}
            manualRetryRequired={manualRetryRequired}
            data={data}
            handleRetry={handleRetry}
            timeLeft={timeLeft}
            allowExtend={allowExtend}
            resetStartTime={resetStartTime}
          />
        </Box>
      )}
    </>
  );
}

interface QRButtonProps {
  size: number;
  qrButtonRef: MutableRefObject<HTMLButtonElement | null> | undefined;
  manualRetryRequired: boolean;
  data: string | undefined;
  handleRetry: (resetStart?: boolean) => void;
  timeLeft: number;
  allowExtend: boolean;
  resetStartTime: () => void;
  onClick?: () => void;
}

function QRButton({
  size,
  qrButtonRef,
  manualRetryRequired,
  data,
  handleRetry,
  timeLeft,
  allowExtend,
  resetStartTime,
  onClick,
}: QRButtonProps) {
  const { t } = useTranslation();
  const qrClasses = qrStyles({ manualRetryRequired });

  const handleClick = () => {
    if (manualRetryRequired) {
      handleRetry(true);
      return;
    }
    if (onClick) onClick();
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <IconButton
        ref={qrButtonRef}
        className={qrClasses.qrButton}
        onClick={handleClick}
        disableRipple
      >
        <Box position="relative" style={{ height: size, width: size }}>
          {data && <QRCode value={data} size={size} />}
          {!data && !manualRetryRequired && (
            <Box className={qrClasses.qrPlaceholder}>
              <img src={BankIDLogo} alt={'BankID logo'} width={64} height={64} />
            </Box>
          )}
          {manualRetryRequired && (
            <Box className={qrClasses.retryIcon} role="button" aria-label="Retry scan">
              <RefreshIcon style={{ fontSize: '9rem' }} />
            </Box>
          )}
        </Box>
      </IconButton>

      <Typography>
        {timeLeft > 0 &&
          `${Math.floor(timeLeft / 60)}:${(timeLeft % 60).toString().padStart(2, '0')}`}
      </Typography>
      {allowExtend && (
        <Button
          variant="contained"
          color="primary"
          style={{ marginTop: '8px' }}
          onClick={resetStartTime}
        >
          {t('bankID.extend')}
        </Button>
      )}
    </Box>
  );
}

function LegalTextModal({
  legalText,
  open,
  setOpen,
}: {
  legalText: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm">
      <Box padding={'2rem'}>
        <DialogTitle disableTypography>
          <Typography variant="h5">{t('bankID.agreementToSign')}</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>{legalText}</Typography>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
